import { useState, useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { getApiBridge, ApiError } from '@packages/api';
import { getMe } from '@packages/api/endpoints/user.js';
import { logout as logoutRequest } from '@packages/api/endpoints/auth.js';
import { isTokenExpired, getAccountIdFromToken } from './token.js';
const SESSION_RESTORE_RETRY_MS = 2_000;
const SESSION_RESTORE_MAX_RETRIES = 5;
/**
 * Core auth state hook - platform-agnostic.
 * Each platform wraps this in their own AuthProvider with createContext.
 */
export function useAuthState() {
    const bridge = getApiBridge();
    const initialToken = useSyncExternalStore(() => () => { }, () => bridge.getToken());
    const hasValidToken = initialToken !== null && !isTokenExpired(initialToken);
    const [user, setUser] = useState(null);
    const [accountId, setAccountId] = useState(null);
    const [isInitializing, setIsInitializing] = useState(true);
    const [isLoading] = useState(false);
    const retryTimerRef = useRef(undefined);
    const login = useCallback((token, profile) => {
        bridge.setToken(token);
        setUser(profile);
        const acc = getAccountIdFromToken(token);
        setAccountId(acc);
    }, [bridge]);
    const logout = useCallback(() => {
        // Fire-and-forget server-side invalidation: bumps the user's tokenVersion so
        // the now-discarded JWT stops working on subsequent requests. We deliberately
        // do NOT await - local state must clear even if the request fails (offline,
        // backend down). The token is already expiring in at most 4h, so a failed
        // server call leaves us no worse off than the old client-only logout.
        void logoutRequest().catch(() => {
            // Intentionally ignored - client-side logout must still proceed.
        });
        bridge.removeToken();
        setUser(null);
        setAccountId(null);
    }, [bridge]);
    const refreshUser = useCallback(async () => {
        const profile = await getMe();
        setUser(profile);
    }, []);
    // Restore session on mount
    useEffect(() => {
        if (!hasValidToken) {
            if (initialToken) {
                bridge.removeToken();
            }
            setIsInitializing(false);
            return;
        }
        const acc = getAccountIdFromToken(initialToken);
        setAccountId(acc);
        let cancelled = false;
        let retries = 0;
        function tryRestore() {
            getMe()
                .then((profile) => {
                if (!cancelled)
                    setUser(profile);
            })
                .catch((error) => {
                if (cancelled)
                    return;
                // 401 means the token is truly invalid - clear it
                if (error instanceof ApiError && error.status === 401) {
                    bridge.removeToken();
                    setAccountId(null);
                    return;
                }
                // Network/server errors (backend restarting) - retry
                if (retries < SESSION_RESTORE_MAX_RETRIES) {
                    retries++;
                    retryTimerRef.current = setTimeout(tryRestore, SESSION_RESTORE_RETRY_MS);
                    return;
                }
                // Exhausted retries - clear token as last resort
                bridge.removeToken();
                setAccountId(null);
            })
                .finally(() => {
                if (!cancelled)
                    setIsInitializing(false);
            });
        }
        tryRestore();
        return () => {
            cancelled = true;
            clearTimeout(retryTimerRef.current);
        };
    }, [hasValidToken, initialToken, bridge]);
    const isAuthenticated = user !== null;
    return { user, accountId, isAuthenticated, isInitializing, isLoading, login, logout, refreshUser };
}
